import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';
import { useMenuAnclado } from './useMenuAnclado';

interface CustomSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface CustomSelectProps {
  options: CustomSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export const CustomSelect: React.FC<CustomSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Seleccionar...',
  disabled = false,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  // Portal a <body> + posición anclada al botón: ver `useMenuAnclado`.
  const { wrapRef, btnRef, menuRef, pos } = useMenuAnclado(open, setOpen);

  const selected = options.find((o) => o.value === value);

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        className={`w-full h-10 flex items-center justify-between px-3 border border-neutral-200 rounded-md text-[15px] bg-surface-card transition-colors ${
          disabled
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:bg-surface-elevated focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary'
        } outline-none`}
      >
        <span className={selected ? 'text-brand-black/90 truncate' : 'text-neutral-400 truncate'}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 text-neutral-400 shrink-0 ml-2 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && pos && createPortal(
        <div
          ref={menuRef}
          className="bg-surface-card border border-neutral-200 rounded-md shadow-md py-1 overflow-y-auto"
          style={{
            position: 'fixed',
            left: pos.left,
            width: pos.width,
            top: pos.top,
            bottom: pos.bottom,
            maxHeight: pos.maxHeight,
            zIndex: 100000,
          }}
        >
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              disabled={opt.disabled}
              onClick={() => {
                if (!opt.disabled) {
                  onChange(opt.value);
                  setOpen(false);
                }
              }}
              className={`w-full text-left px-3 py-2 text-[15px] flex items-center justify-between transition-colors ${
                opt.disabled
                  ? 'text-neutral-300 cursor-not-allowed'
                  : value === opt.value
                    ? 'bg-neutral-100 text-brand-black/90 font-medium'
                    : 'text-neutral-600 hover:bg-neutral-100'
              }`}
            >
              <span className="truncate">{opt.label}</span>
              {value === opt.value && !opt.disabled && (
                <Check className="w-4 h-4 text-brand-primary shrink-0 ml-2" />
              )}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
};
